import { UploadIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { fetchEnergyPassport } from "@/entities/energy-passport/api/fetch-energy-passport.ts"
import type { IEnergyPassportProduct } from "@/entities/energy-passport/types.ts"
import { useFetchAuditRequestById } from "@/features/audit-request/api/use-fetch-audit-request-by-id.ts"
import { useFetchEnergyPassport } from "@/features/energy-passport/api/use-fetch-energy-passport.ts"
import { useSaveEnergyPassport } from "@/features/energy-passport/api/use-save-energy-passport.ts"
import { EnergyPassportCoalTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-coal-table.tsx"
import { EnergyPassportDieselTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-diesel-table.tsx"
import { EnergyPassportGasTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-gas-table.tsx"
import { EnergyPassportKeroseneTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-kerosene-table.tsx"
import { EnergyPassportLubricantTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-lubricant-table.tsx"
import { EnergyPassportMazutTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-mazut-table.tsx"
import { EnergyPassportOtherOilProductsTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-other-oil-products-table.tsx"
import { EnergyPassportPetrolTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-petrol-table.tsx"
import { EnergyPassportThermalEnergyTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-thermal-energy-table.tsx"
import { EnergyPassportWaterTable } from "@/features/energy-passport/ui/detail-tables/energy-passport-water-table.tsx"
import { initialEnergyPassportDetailsStore, useEnergyPassportDetailsStore } from "@/features/energy-passport/ui/detail-tables/model/store.ts"
import { EnergyPassportElectricPowerTable } from "@/features/energy-passport/ui/electric-power-table/energy-passport-electric-power-table.tsx"
import { EnergyPassportQuarterTable } from "@/features/energy-passport/ui/main-table/energy-passport-quarter-table.tsx"
import { energyPassportInitialQuarterData, useEnergyPassportStore } from "@/features/energy-passport/ui/main-table/model/store.ts"
import { Loader } from "@/shared/components/ui/icons"
import { useCurrentUser } from "@/shared/hooks/use-current-user.ts"
import { cn } from "@/shared/lib/utils.ts"
import type { IQuarterNumber } from "@/shared/types/interfaces"
import { Button } from "@/shared/ui/button"
import { Notice } from "@/shared/utils/notice"

export function EnergyPassportPage() {
  // Helpers
  const { contractId, year } = useParams()
  const { isCompany } = useCurrentUser()
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get("requestId")

  // Store
  const { quarters, setQuarters, notifyInvalidProduct } = useEnergyPassportStore()
  const {
    heat,
    mazut,
    coal,
    gas,
    petrol,
    kerosene,
    diesel,
    lubricant,
    other_oil_product,
    water,
    active_electricity,
    reactive_electricity,
    setDetails
  } = useEnergyPassportDetailsStore()

  // States
  const [yearState, setYearState] = useState<string | undefined>(year)

  // Queries
  const { mutate: saveEnergyPassport, isPending } = useSaveEnergyPassport()
  const { data: audit, isPending: isPendingEnergyAudit } = useFetchAuditRequestById(requestId || undefined)
  const {
    data: response,
    isPending: loading,
    isFetching,
    refetch
  } = useFetchEnergyPassport({
    year: yearState,
    contractId
  })
  const data = response?.quarters
  const details = response?.details

  const endYear = new Date(audit?.created_at || new Date()).getFullYear()

  // Effects
  useEffect(() => {
    setYearState(year)
  }, [year])

  useEffect(() => {
    if (data?.length) {
      setQuarters(
        energyPassportInitialQuarterData.map(quarter => {
          const savedQuarter = data.find(q => q.quarter_number === quarter.quarterNumber)
          if (savedQuarter) {
            return {
              deletedProductIds: [],
              expanded: !!quarters.find(q => q.quarterNumber === savedQuarter.quarter_number)?.expanded,
              quarterNumber: savedQuarter.quarter_number as IQuarterNumber,
              products: savedQuarter.products
            }
          } else {
            return quarter
          }
        })
      )
    } else {
      if (yearState && contractId) {
        const startYear = endYear - 4
        const year = startYear < Number.parseInt(yearState) ? Number.parseInt(yearState) - 1 : startYear

        fetchEnergyPassport({ year, contractId }).then(prevData => {
          if (prevData) {
            const prevDataQuarters = prevData?.quarters
            console.log({ prevDataQuarters })
            setQuarters(
              energyPassportInitialQuarterData.map(quarter => {
                const savedQuarter = prevDataQuarters.find(q => q.quarter_number === quarter.quarterNumber)
                if (savedQuarter) {
                  return {
                    deletedProductIds: [],
                    expanded: !!quarters.find(q => q.quarterNumber === savedQuarter.quarter_number)?.expanded,
                    quarterNumber: savedQuarter.quarter_number as IQuarterNumber,
                    products: savedQuarter.products.map((p, i) => ({ ...p, tempId: "id-" + i, id: undefined }))
                  }
                }
                return quarter
              })
            )
          } else {
            setQuarters(energyPassportInitialQuarterData)
          }
        })
      } else {
        setQuarters(energyPassportInitialQuarterData)
      }
    }
  }, [data, yearState])

  useEffect(() => {
    if (details) {
      const store = structuredClone(initialEnergyPassportDetailsStore)
      for (const quarter of details.quarters) {
        const type = quarter.energy_type
        const quarters = store[type].quarters
        const index = quarters.findIndex(q => q.quarter === quarter.quarter)
        if (index !== -1) {
          quarters[index] = quarter
        } else {
          quarters.push(quarter)
          store[type].expanded = true
        }
      }

      setDetails(store)
    } else {
      setDetails(initialEnergyPassportDetailsStore)
    }
  }, [details])

  // Functions
  const findInvalidProduct = (products: IEnergyPassportProduct[]) => {
    return products.find(p => p.nameError)
  }

  const handleSaveEnergyPassport = () => {
    if (contractId && year) {
      let deleted_products: number[] = []

      const products = quarters.flatMap(quarter => {
        deleted_products = deleted_products.concat(quarter.deletedProductIds)
        return quarter.products.map(product => ({
          ...product,
          quarter: undefined,
          quarter_number: quarter.quarterNumber
        }))
      })

      // Filter empty products
      const filtered = products.filter(p => {
        return (
          p.reactive_energy ||
          p.active_energy ||
          p.thermal_energy ||
          p.oils ||
          p.water ||
          p.count ||
          p.gasoline ||
          p.diesel ||
          p.kerosene ||
          p.other_petroleum_products ||
          p.mazut ||
          p.gas ||
          p.coal
        )
      })

      const invalidProduct = findInvalidProduct(filtered)

      if (!invalidProduct) {
        saveEnergyPassport(
          {
            params: { contractId, year },
            payload: {
              products: filtered,
              deleted_products,
              details: active_electricity.quarters
                .concat(reactive_electricity.quarters)
                .concat(heat.quarters)
                .concat(gas.quarters)
                .concat(mazut.quarters)
                .concat(coal.quarters)
                .concat(petrol.quarters)
                .concat(kerosene.quarters)
                .concat(diesel.quarters)
                .concat(lubricant.quarters)
                .concat(other_oil_product.quarters)
                .concat(water.quarters)
            }
          },
          {
            onSuccess: () => {
              Notice.success("Энергетический паспорт успешно сохранен")
            }
          }
        )
      } else {
        notifyInvalidProduct(invalidProduct)
      }
    }
  }

  return (
    <div className={cn("relative flex-1 pb-2", !loading && "overflow-auto")}>
      {loading && isFetching ? (
        <div
          style={{ animationDuration: "700ms" }}
          className="absolute inset-0 z-[2000] flex animate-pulse justify-center rounded-xl bg-[rgba(240,240,240,.1)] backdrop-blur-[3px]"
        >
          <Loader className="mt-40 size-10 animate-spin *:fill-blue-600" style={{ animationDuration: "300ms" }} />
        </div>
      ) : null}
      <EnergyPassportQuarterTable />

      {/* Details Tables */}
      <EnergyPassportElectricPowerTable />
      <EnergyPassportThermalEnergyTable />
      <EnergyPassportGasTable />
      <EnergyPassportMazutTable />
      <EnergyPassportCoalTable />
      <EnergyPassportPetrolTable />
      <EnergyPassportDieselTable />
      <EnergyPassportKeroseneTable />
      <EnergyPassportLubricantTable />
      <EnergyPassportOtherOilProductsTable />
      <EnergyPassportWaterTable />

      <Button
        variant="contained"
        loading={isPending}
        color="primary"
        onClick={handleSaveEnergyPassport}
        startIcon={<UploadIcon size={15} />}
        className={cn("my-4 ml-auto text-nowrap bg-blue-600", isCompany && "hidden")}
      >
        Сохранить и продолжить
      </Button>
    </div>
  )
}
